import React, { useEffect, useState } from 'react';
import { api, uploadFile, extractApiError } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Save, MapPin, FileText, Tag, ShieldCheck, ShieldOff, Megaphone, Upload, BarChart3, Globe, ExternalLink, Radar } from 'lucide-react';
import SearchInput from '@/components/marketplace/SearchInput';
import SEOFieldsBlock from '@/components/marketplace/SEOFieldsBlock';
import FramedImage from '@/components/marketplace/FramedImage';
import SEOScoreBadge from '@/components/marketplace/SEOScoreBadge';
import RichTextEditor from '@/components/marketplace/RichTextEditor';
import IndexingTab from '@/components/marketplace/IndexingTab';
import SectionPagesTab from '@/components/marketplace/SectionPagesTab';

const emptyPage = { slug: '', title: '', html_content: '', meta_title: '', meta_description: '', active: true };
const emptyLocPage = { city: '', state: '', slug: '', title: '', html_content: '', hero_image_url: '', hero_image_alt: '', hero_image_caption: '', published: true, display_order: 0, meta_title: '', meta_description: '' };
const emptyLoc = { state: '', city: '', country: 'India' };
const emptyBlogCat = { name: '', description: '', display_order: 0, active: true };
const emptyAd = {
  title: '', subtitle: '', image_url: '', click_url: '',
  placement: 'listing_grid', listing_type: null, active: true,
  display_order: 0, advertiser_name: '', notes: '',
  starts_at: null, ends_at: null,
};

// ---- IST <-> UTC conversion helpers for datetime-local input ----
// India Standard Time is UTC+5:30. datetime-local inputs use naive local time,
// so we treat the input as IST and convert to UTC ISO on save (and vice versa
// when loading an existing value from the backend).
const IST_OFFSET_MIN = 5 * 60 + 30;

const utcIsoToIstInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  // Get IST wall-clock by adding IST_OFFSET to UTC ms
  const istMs = d.getTime() + IST_OFFSET_MIN * 60 * 1000;
  const ist = new Date(istMs);
  const pad = (n) => String(n).padStart(2, '0');
  return `${ist.getUTCFullYear()}-${pad(ist.getUTCMonth() + 1)}-${pad(ist.getUTCDate())}T${pad(ist.getUTCHours())}:${pad(ist.getUTCMinutes())}`;
};

const istInputToUtcIso = (local) => {
  if (!local) return null;
  // Parse "YYYY-MM-DDTHH:mm" as IST wall-clock time
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(local);
  if (!m) return null;
  const [, y, mo, d, h, mi] = m;
  // Convert IST to UTC by subtracting the offset
  const utcMs = Date.UTC(+y, +mo - 1, +d, +h, +mi) - IST_OFFSET_MIN * 60 * 1000;
  return new Date(utcMs).toISOString();
};


const AdminCMS = () => {
  return (
    <div className='space-y-5'>
      <div>
        <h1 className='text-2xl sm:text-3xl font-semibold tracking-tight'>Content Management</h1>
        <p className='text-muted-foreground text-sm'>Manage static pages, locations, blog categories, and GST verifications.</p>
      </div>
      <Tabs defaultValue='pages'>
        <TabsList data-testid='admin-cms-tabs'>
          <TabsTrigger value='pages' data-testid='cms-tab-pages'><FileText className='h-4 w-4 mr-1' /> Static Pages</TabsTrigger>
          <TabsTrigger value='locations' data-testid='cms-tab-locations'><MapPin className='h-4 w-4 mr-1' /> Locations</TabsTrigger>
          <TabsTrigger value='location-pages' data-testid='cms-tab-location-pages'><Globe className='h-4 w-4 mr-1' /> Location Pages</TabsTrigger>
          <TabsTrigger value='blog-cats' data-testid='cms-tab-blog-cats'><Tag className='h-4 w-4 mr-1' /> Blog Categories</TabsTrigger>
          <TabsTrigger value='gst' data-testid='cms-tab-gst'><ShieldCheck className='h-4 w-4 mr-1' /> GST Verification</TabsTrigger>
          <TabsTrigger value='ads' data-testid='cms-tab-ads'><Megaphone className='h-4 w-4 mr-1' /> Sponsored Ads</TabsTrigger>
          <TabsTrigger value='analytics' data-testid='cms-tab-analytics'><BarChart3 className='h-4 w-4 mr-1' /> Analytics</TabsTrigger>
          <TabsTrigger value='indexing' data-testid='cms-tab-indexing'><Radar className='h-4 w-4 mr-1' /> Indexing</TabsTrigger>
          <TabsTrigger value='sections' data-testid='cms-tab-sections'><Globe className='h-4 w-4 mr-1' /> Sections</TabsTrigger>
        </TabsList>

        <TabsContent value='pages' className='mt-4'><StaticPagesTab /></TabsContent>
        <TabsContent value='locations' className='mt-4'><LocationsTab /></TabsContent>
        <TabsContent value='location-pages' className='mt-4'><LocationPagesTab /></TabsContent>
        <TabsContent value='blog-cats' className='mt-4'><BlogCatsTab /></TabsContent>
        <TabsContent value='gst' className='mt-4'><GstVerifyTab /></TabsContent>
        <TabsContent value='ads' className='mt-4'><SponsoredAdsTab /></TabsContent>
        <TabsContent value='analytics' className='mt-4'><AnalyticsTab /></TabsContent>
        <TabsContent value='indexing' className='mt-4'><IndexingTab /></TabsContent>
        <TabsContent value='sections' className='mt-4'><SectionPagesTab /></TabsContent>
      </Tabs>
    </div>
  );
};

// ============================================================
// Static Pages
// ============================================================
const StaticPagesTab = () => {
  const [pages, setPages] = useState([]);
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = () => api.get('/static-pages').then(r => setPages(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.title || !editing.slug) return toast.error('Title and slug are required');
    try {
      if (editing.id) {
        await api.patch(`/static-pages/${editing.id}`, editing);
        toast.success('Page updated');
      } else {
        await api.post('/static-pages', editing);
        toast.success('Page created');
      }
      setDialogOpen(false); setEditing(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this page?')) return;
    try { await api.delete(`/static-pages/${id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <div className='space-y-3'>
      <div className='flex justify-end'>
        <Button onClick={() => { setEditing({ ...emptyPage }); setDialogOpen(true); }} data-testid='cms-new-page'>
          <Plus className='h-4 w-4 mr-1' /> New page
        </Button>
      </div>
      <Card className='divide-y'>
        {pages.length === 0 && <div className='p-8 text-center text-muted-foreground text-sm'>No pages yet.</div>}
        {pages.map(p => (
          <div key={p.id} className='p-4 flex items-center justify-between gap-3' data-testid={`cms-page-row-${p.slug}`}>
            <div className='min-w-0 flex-1'>
              <div className='flex items-center gap-2 flex-wrap'>
                <div className='font-semibold'>{p.title} <span className='text-xs text-muted-foreground'>/{p.slug}</span></div>
                <SEOScoreBadge score={p.seo_score} summary={p.seo_score_summary} />
              </div>
              <div className='text-xs text-muted-foreground'>{p.active ? 'Active' : 'Inactive'} · {p.meta_description?.slice(0, 100) || 'No meta description'}</div>
            </div>
            <div className='flex gap-2 shrink-0'>
              <Button size='sm' variant='outline' onClick={() => { setEditing(p); setDialogOpen(true); }} data-testid={`cms-edit-page-${p.slug}`}>Edit</Button>
              <Button size='sm' variant='outline' onClick={() => remove(p.id)} data-testid={`cms-delete-page-${p.slug}`}><Trash2 className='h-3.5 w-3.5' /></Button>
            </div>
          </div>
        ))}
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className='max-w-3xl max-h-[85vh] overflow-y-auto' onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
          <DialogHeader><DialogTitle>{editing?.id ? 'Edit page' : 'New page'}</DialogTitle></DialogHeader>
          {editing && (
            <div className='space-y-3'>
              <div className='grid grid-cols-2 gap-3'>
                <div>
                  <Label>Slug</Label>
                  <Input value={editing.slug} disabled={!!editing.id} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} data-testid='cms-page-slug' />
                </div>
                <div>
                  <Label>Title</Label>
                  <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid='cms-page-title' />
                </div>
              </div>
              <div>
                <Label>Content</Label>
                <RichTextEditor
                  value={editing.html_content || ''}
                  onChange={(html) => setEditing({ ...editing, html_content: html })}
                />
                <div className='text-[11px] text-muted-foreground mt-1'>Use headings, tables, lists, colours, and images to build a full page — HTML is generated automatically. Scripts are stripped on save.</div>
              </div>
              <div className='flex items-center gap-2'>
                <Switch checked={!!editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} />
                <Label>Active (public)</Label>
              </div>

              {/* Shared SEO block — Review.pdf #11 */}
              <div className='pt-4 border-t'>
                <SEOFieldsBlock
                  values={editing}
                  onChange={(patch) => setEditing({ ...editing, ...patch })}
                  allowAdvanced
                  contentType='page'
                  testIdPrefix='cms-page-seo'
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant='outline' onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid='cms-page-save'><Save className='h-4 w-4 mr-1' /> Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================================================
// Locations
// ============================================================
const LocationsTab = () => {
  const [locs, setLocs] = useState([]);
  const [q, setQ] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [states, setStates] = useState([]);
  const [newLoc, setNewLoc] = useState({ ...emptyLoc });

  const load = () => {
    const params = {};
    if (q) params.q = q;
    if (stateFilter) params.state = stateFilter;
    api.get('/locations', { params }).then(r => setLocs(r.data));
  };
  useEffect(() => {
    api.get('/locations/states').then(r => setStates(r.data));
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [stateFilter, q]);

  const add = async () => {
    if (!newLoc.state || !newLoc.city) return toast.error('State and city required');
    try { await api.post('/locations', newLoc); setNewLoc({ ...emptyLoc }); toast.success('Added'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this location?')) return;
    try { await api.delete(`/locations/${id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <div className='space-y-3'>
      <Card className='p-4 grid md:grid-cols-4 gap-3'>
        <div>
          <Label className='text-xs'>Search</Label>
          <SearchInput
            value={q}
            onChange={setQ}
            onDebouncedChange={setQ}
            placeholder='city/state'
            testid='cms-loc-search'
          />
        </div>
        <div>
          <Label className='text-xs'>State filter</Label>
          <Select value={stateFilter || '__all__'} onValueChange={(v) => setStateFilter(v === '__all__' ? '' : v)}>
            <SelectTrigger><SelectValue placeholder='All states' /></SelectTrigger>
            <SelectContent>
              <SelectItem value='__all__'>All states</SelectItem>
              {states.map(s => <SelectItem key={s.state} value={s.state}>{s.state}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card className='p-4'>
        <div className='font-semibold text-sm mb-2'>Add new location</div>
        <div className='grid md:grid-cols-4 gap-2'>
          <Input placeholder='State' value={newLoc.state} onChange={(e) => setNewLoc({ ...newLoc, state: e.target.value })} data-testid='cms-loc-state' />
          <Input placeholder='City' value={newLoc.city} onChange={(e) => setNewLoc({ ...newLoc, city: e.target.value })} data-testid='cms-loc-city' />
          <Input placeholder='Country' value={newLoc.country} onChange={(e) => setNewLoc({ ...newLoc, country: e.target.value })} />
          <Button onClick={add} data-testid='cms-loc-add'><Plus className='h-4 w-4 mr-1' /> Add</Button>
        </div>
      </Card>

      <Card className='p-3'>
        <div className='text-xs text-muted-foreground mb-2'>{locs.length} location(s)</div>
        <div className='max-h-[560px] overflow-y-auto'>
          <table className='w-full text-sm'>
            <thead>
              <tr className='text-left border-b'>
                <th className='py-2 pr-3'>State</th>
                <th className='py-2 pr-3'>City</th>
                <th className='py-2 pr-3'>Country</th>
                <th className='py-2 pr-3 text-right'>Actions</th>
              </tr>
            </thead>
            <tbody>
              {locs.map(l => (
                <tr key={l.id} className='border-b last:border-b-0'>
                  <td className='py-2 pr-3'>{l.state}</td>
                  <td className='py-2 pr-3 font-medium'>{l.city}</td>
                  <td className='py-2 pr-3 text-xs text-muted-foreground'>{l.country || 'India'}</td>
                  <td className='py-2 pr-3 text-right'>
                    <Button size='sm' variant='outline' onClick={() => remove(l.id)} data-testid={`cms-loc-delete-${l.id}`}><Trash2 className='h-3 w-3' /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

// ============================================================
// Location Pages (SEO city landing pages — full CMS)
// ============================================================
const LocationPagesTab = () => {
  const [pages, setPages] = useState([]);
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploadingHero, setUploadingHero] = useState(false);
  const [draftRestored, setDraftRestored] = useState(false);
  const DRAFT_KEY = 'indusfact_locpage_draft';

  const load = () => api.get('/location-pages/admin/all').then(r => setPages(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.city || !editing.state) return toast.error('City and state are required');
    try {
      if (editing.id) {
        await api.patch(`/location-pages/${editing.id}`, editing);
        toast.success('Location page updated');
      } else {
        await api.post('/location-pages', editing);
        toast.success('Location page created');
      }
      clearDraft();
      setDialogOpen(false); setEditing(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  };

  const togglePublish = async (p) => {
    try { await api.patch(`/location-pages/${p.id}`, { published: !p.published }); load(); }
    catch { toast.error('Failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this location page?')) return;
    try { await api.delete(`/location-pages/${id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  // Canonical location-hero canvas. Used for the recommended-size hint and
  // the non-blocking aspect-ratio warning (we never auto-crop/stretch).
  const HERO_TARGET_RATIO = 1336 / 420; // ≈ 3.18

  const uploadHero = async (file) => {
    if (!file) return;
    // Read the source dimensions first and warn (do NOT block/crop) if the
    // aspect ratio differs meaningfully from the 1336×420 banner canvas.
    try {
      const dims = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
        img.onerror = reject;
        img.src = URL.createObjectURL(file);
      });
      if (dims.w && dims.h) {
        const r = dims.w / dims.h;
        if (Math.abs(r - HERO_TARGET_RATIO) / HERO_TARGET_RATIO > 0.08) {
          toast.warning(
            `Recommended ratio 3.18:1 (1336×420). Your image is ${dims.w}×${dims.h} (${r.toFixed(2)}:1) — it will be fitted proportionally without stretching or cropping.`,
            { duration: 6000 },
          );
        }
      }
    } catch { /* dimension read is best-effort */ }
    setUploadingHero(true);
    try {
      const r = await uploadFile(file, 'image', { context: 'location_hero' });
      const url = r.public_url?.startsWith('http') ? r.public_url : `${process.env.REACT_APP_BACKEND_URL || ''}${r.public_url}`;
      setEditing((e) => ({ ...e, hero_image_url: url }));
      toast.success('Hero image uploaded');
    } catch { toast.error('Upload failed'); }
    finally { setUploadingHero(false); }
  };

  // ---- Draft safety net: auto-save an in-progress NEW page to localStorage
  // so an accidental close/refresh never loses work. Cleared on save/discard.
  const clearDraft = () => {
    try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
    setDraftRestored(false);
  };
  const discardDraft = () => { clearDraft(); setEditing({ ...emptyLocPage }); };
  const openNew = () => {
    let start = { ...emptyLocPage };
    let restored = false;
    try {
      const saved = localStorage.getItem(DRAFT_KEY);
      if (saved) { start = { ...emptyLocPage, ...JSON.parse(saved) }; restored = true; }
    } catch { /* ignore */ }
    setDraftRestored(restored);
    setEditing(start);
    setDialogOpen(true);
  };
  useEffect(() => {
    if (dialogOpen && editing && !editing.id) {
      try { localStorage.setItem(DRAFT_KEY, JSON.stringify(editing)); } catch { /* ignore */ }
    }
  }, [editing, dialogOpen]);

  return (
    <div className='space-y-3'>
      <div className='flex items-center justify-between'>
        <p className='text-sm text-muted-foreground'>SEO landing pages per city — content, listing feed and full meta controls. Auto-added to sitemap.</p>
        <Button onClick={openNew} data-testid='cms-new-location-page'>
          <Plus className='h-4 w-4 mr-1' /> New location page
        </Button>
      </div>
      <Card className='divide-y'>
        {pages.length === 0 && <div className='p-8 text-center text-muted-foreground text-sm'>No location pages yet.</div>}
        {pages.map(p => (
          <div key={p.id} className='p-4 flex items-center justify-between gap-3' data-testid={`cms-locpage-row-${p.slug}`}>
            <div className='min-w-0 flex-1'>
              <div className='flex items-center gap-2 flex-wrap'>
                <div className='font-semibold'>{p.city}, {p.state} <span className='text-xs text-muted-foreground'>/locations/{p.slug}</span></div>
                <SEOScoreBadge score={p.seo_score} summary={p.seo_score_summary} />
                <span className={`text-[10px] rounded-full px-1.5 py-0.5 font-medium ${p.published ? 'bg-[hsl(var(--verified))]/10 text-[hsl(var(--verified))]' : 'bg-muted text-muted-foreground'}`}>
                  {p.published ? 'Published' : 'Draft'}
                </span>
              </div>
              <div className='text-xs text-muted-foreground'>{p.listing_count || 0} live listings · {p.meta_description?.slice(0, 90) || 'No meta description'}</div>
            </div>
            <div className='flex gap-2 shrink-0'>
              <Button size='sm' variant='outline' asChild>
                <a href={`/locations/${p.slug}`} target='_blank' rel='noreferrer' data-testid={`cms-locpage-preview-${p.slug}`}><ExternalLink className='h-3.5 w-3.5' /></a>
              </Button>
              <Button size='sm' variant='outline' onClick={() => togglePublish(p)} data-testid={`cms-locpage-toggle-${p.slug}`}>{p.published ? 'Unpublish' : 'Publish'}</Button>
              <Button size='sm' variant='outline' onClick={() => { setEditing(p); setDialogOpen(true); }} data-testid={`cms-edit-locpage-${p.slug}`}>Edit</Button>
              <Button size='sm' variant='outline' onClick={() => remove(p.id)} data-testid={`cms-delete-locpage-${p.slug}`}><Trash2 className='h-3.5 w-3.5' /></Button>
            </div>
          </div>
        ))}
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          className='max-w-3xl max-h-[85vh] overflow-y-auto'
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader><DialogTitle>{editing?.id ? 'Edit location page' : 'New location page'}</DialogTitle>
            <DialogDescription>SEO city landing page — content, listing feed and full meta controls.</DialogDescription>
          </DialogHeader>
          {editing && !editing.id && (
            <div className='text-xs rounded-md bg-secondary/60 px-3 py-2 flex items-center justify-between gap-2' data-testid='locpage-draft-note'>
              <span>{draftRestored ? 'Draft restored — your unsaved changes were recovered.' : 'Changes auto-save as a draft, so nothing is lost if you close by accident.'}</span>
              <button type='button' className='text-primary hover:underline shrink-0' onClick={discardDraft} data-testid='locpage-discard-draft'>Discard draft</button>
            </div>
          )}
          {editing && (
            <div className='space-y-3'>
              <div className='grid grid-cols-3 gap-3'>
                <div>
                  <Label>City *</Label>
                  <Input value={editing.city} onChange={(e) => setEditing({ ...editing, city: e.target.value })} data-testid='cms-locpage-city' />
                </div>
                <div>
                  <Label>State *</Label>
                  <Input value={editing.state} onChange={(e) => setEditing({ ...editing, state: e.target.value })} data-testid='cms-locpage-state' />
                </div>
                <div>
                  <Label>Slug</Label>
                  <Input value={editing.slug} placeholder='auto from city' onChange={(e) => setEditing({ ...editing, slug: e.target.value })} data-testid='cms-locpage-slug' />
                </div>
              </div>
              <div>
                <Label>Page title (H1)</Label>
                <Input value={editing.title} placeholder='Auto-generated from city if left empty' onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid='cms-locpage-title' />
              </div>
              <div>
                <Label>Hero image (optional)</Label>
                <div className='text-[11px] text-muted-foreground mb-2' data-testid='cms-locpage-hero-recommendation'>
                  Recommended: <strong>1336 × 420 px</strong> (3.18:1). Any ratio is accepted — it is fitted proportionally, never stretched or cropped.
                </div>
                <div className='flex items-center gap-3'>
                  <label className='inline-flex items-center gap-1.5 rounded-md border px-3 py-2 cursor-pointer text-sm hover:bg-secondary'>
                    <Upload className='h-4 w-4' /> {uploadingHero ? 'Uploading…' : (editing.hero_image_url ? 'Replace' : 'Upload')}
                    <input type='file' accept='image/*' className='hidden' onChange={(e) => { uploadHero(e.target.files?.[0]); e.target.value = ''; }} data-testid='cms-locpage-hero-upload' />
                  </label>
                  {editing.hero_image_url && <Button size='sm' variant='ghost' onClick={() => setEditing({ ...editing, hero_image_url: '', hero_image_alt: '', hero_image_caption: '' })}>Remove</Button>}
                </div>
                {editing.hero_image_url && (
                  <div className='mt-3 max-w-md'>
                    <div className='text-[11px] uppercase tracking-wider text-muted-foreground mb-1'>Live preview (how it renders on the page)</div>
                    <FramedImage
                      src={editing.hero_image_url}
                      alt={editing.hero_image_alt || 'hero preview'}
                      ratio='aspect-[1336/420]'
                      frameClassName='rounded-lg border'
                      testId='cms-locpage-hero-preview'
                    />
                  </div>
                )}
                {editing.hero_image_url && (
                  <div className='grid sm:grid-cols-2 gap-3 mt-3'>
                    <div>
                      <Label className='text-xs'>Hero alt text</Label>
                      <Input
                        value={editing.hero_image_alt || ''}
                        placeholder='Auto-filled from focus keyword if left empty'
                        maxLength={200}
                        onChange={(e) => setEditing({ ...editing, hero_image_alt: e.target.value })}
                        data-testid='cms-locpage-hero-alt'
                      />
                      <div className='text-[11px] text-muted-foreground mt-1'>Counts towards the SEO image score.</div>
                    </div>
                    <div>
                      <Label className='text-xs'>Hero caption (optional)</Label>
                      <Input
                        value={editing.hero_image_caption || ''}
                        placeholder='Shown under the hero image'
                        maxLength={200}
                        onChange={(e) => setEditing({ ...editing, hero_image_caption: e.target.value })}
                        data-testid='cms-locpage-hero-caption'
                      />
                    </div>
                  </div>
                )}
              </div>
              <div>
                <Label>Content</Label>
                <RichTextEditor
                  value={editing.html_content || ''}
                  onChange={(html) => setEditing({ ...editing, html_content: html })}
                />
                <div className='text-[11px] text-muted-foreground mt-1'>Editorial copy for this city. A live listing feed and category links render automatically on the page.</div>
              </div>
              <div className='flex items-center gap-4'>
                <div className='flex items-center gap-2'>
                  <Switch checked={!!editing.published} onCheckedChange={(v) => setEditing({ ...editing, published: v })} data-testid='cms-locpage-published' />
                  <Label>Published</Label>
                </div>
                <div className='flex items-center gap-2'>
                  <Label className='text-xs'>Display order</Label>
                  <Input type='number' className='w-20' value={editing.display_order || 0} onChange={(e) => setEditing({ ...editing, display_order: Number(e.target.value) })} />
                </div>
              </div>

              <div className='pt-4 border-t'>
                <SEOFieldsBlock
                  values={editing}
                  onChange={(patch) => setEditing({ ...editing, ...patch })}
                  allowAdvanced
                  contentType='page'
                  testIdPrefix='cms-locpage-seo'
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant='outline' onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid='cms-locpage-save'><Save className='h-4 w-4 mr-1' /> Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================================================
// Blog Categories
// ============================================================
const BlogCatsTab = () => {
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = () => api.get('/blog/categories', { params: { include_inactive: true } }).then(r => setCats(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.name) return toast.error('Name required');
    try {
      if (editing.id) {
        await api.patch(`/blog/categories/${editing.id}`, editing);
      } else {
        await api.post('/blog/categories', editing);
      }
      toast.success('Saved'); setDialogOpen(false); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this category? Posts will lose their category assignment.')) return;
    try { await api.delete(`/blog/categories/${id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div className='space-y-3'>
      <div className='flex justify-end'>
        <Button onClick={() => { setEditing({ ...emptyBlogCat }); setDialogOpen(true); }} data-testid='cms-new-blogcat'>
          <Plus className='h-4 w-4 mr-1' /> New category
        </Button>
      </div>
      <Card className='divide-y'>
        {cats.length === 0 && <div className='p-8 text-center text-muted-foreground text-sm'>No blog categories.</div>}
        {cats.map(c => (
          <div key={c.id} className='p-4 flex items-center justify-between gap-3'>
            <div>
              <div className='font-semibold'>{c.name} <span className='text-xs text-muted-foreground'>({c.post_count || 0} posts)</span></div>
              <div className='text-xs text-muted-foreground'>{c.description || 'No description'}</div>
            </div>
            <div className='flex gap-2'>
              <Button size='sm' variant='outline' onClick={() => { setEditing(c); setDialogOpen(true); }} data-testid={`cms-edit-blogcat-${c.slug}`}>Edit</Button>
              <Button size='sm' variant='outline' onClick={() => remove(c.id)} data-testid={`cms-delete-blogcat-${c.slug}`}><Trash2 className='h-3.5 w-3.5' /></Button>
            </div>
          </div>
        ))}
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
          <DialogHeader><DialogTitle>{editing?.id ? 'Edit category' : 'New category'}</DialogTitle></DialogHeader>
          {editing && (
            <div className='space-y-3'>
              <div>
                <Label>Name</Label>
                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid='cms-blogcat-name' />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea rows={3} value={editing.description || ''} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
              </div>
              <div className='grid grid-cols-2 gap-3'>
                <div>
                  <Label>Display order</Label>
                  <Input type='number' value={editing.display_order || 0} onChange={(e) => setEditing({ ...editing, display_order: Number(e.target.value) })} />
                </div>
                <div className='flex items-end gap-2'>
                  <Switch checked={!!editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} />
                  <Label>Active</Label>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant='outline' onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid='cms-blogcat-save'><Save className='h-4 w-4 mr-1' /> Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================================================
// GST Verification
// ============================================================
const GstVerifyTab = () => {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState('');

  const load = () => api.get('/admin/users', { params: q ? { q } : {} }).then(r => setUsers(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q]);

  const toggle = async (u, verified) => {
    try {
      await api.post(`/verification/gst/${u.id}/set`, null, { params: { verified } });
      toast.success(`GST ${verified ? 'verified' : 'unverified'}`); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className='space-y-3'>
      <Card className='p-4'>
        <div className='flex gap-2'>
          <SearchInput
            value={q}
            onChange={setQ}
            onDebouncedChange={(v) => { setQ(v); }}
            onSubmit={() => load()}
            placeholder='Search by name, email, company'
            testid='cms-gst-search'
          />
        </div>
      </Card>
      <Card>
        <table className='w-full text-sm'>
          <thead className='text-left'>
            <tr className='border-b'>
              <th className='py-2 px-3'>Name / Company</th>
              <th className='py-2 px-3'>Email</th>
              <th className='py-2 px-3'>Verification Level</th>
              <th className='py-2 px-3'>GST Verified</th>
              <th className='py-2 px-3 text-right'>Action</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} className='border-b last:border-b-0' data-testid={`cms-gst-row-${u.id}`}>
                <td className='py-2 px-3'>
                  <div className='font-medium'>{u.name}</div>
                  <div className='text-xs text-muted-foreground'>{u.company_name || '—'}</div>
                </td>
                <td className='py-2 px-3 text-muted-foreground'>{u.email}</td>
                <td className='py-2 px-3 text-xs'>{u.verification_level?.replace(/_/g, ' ')}</td>
                <td className='py-2 px-3'>
                  {u.is_gst_verified ? (
                    <span className='inline-flex items-center gap-1 text-xs font-medium text-[hsl(var(--verified))]'>
                      <ShieldCheck className='h-3.5 w-3.5' /> Verified
                    </span>
                  ) : (
                    <span className='inline-flex items-center gap-1 text-xs text-muted-foreground'>
                      <ShieldOff className='h-3.5 w-3.5' /> Not verified
                    </span>
                  )}
                </td>
                <td className='py-2 px-3 text-right'>
                  {u.is_gst_verified ? (
                    <Button size='sm' variant='outline' onClick={() => toggle(u, false)} data-testid={`cms-gst-revoke-${u.id}`}>Revoke</Button>
                  ) : (
                    <Button size='sm' onClick={() => toggle(u, true)} data-testid={`cms-gst-verify-${u.id}`}>Mark verified</Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
};

// ============================================================
// Sponsored Ads
// ============================================================
const SponsoredAdsTab = () => {
  const [ads, setAds] = useState([]);
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploadingAd, setUploadingAd] = useState(false);

  const load = () => api.get('/sponsored-ads').then(r => setAds(r.data || []));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.title || !editing.image_url || !editing.click_url) {
      return toast.error('Title, image URL, and click URL are required');
    }
    try {
      const payload = { ...editing };
      if (payload.listing_type === '' || payload.listing_type === 'any') payload.listing_type = null;
      if (payload.starts_at === '') payload.starts_at = null;
      if (payload.ends_at === '') payload.ends_at = null;
      if (editing.id) {
        await api.patch(`/sponsored-ads/${editing.id}`, payload);
      } else {
        await api.post('/sponsored-ads', payload);
      }
      toast.success('Saved'); setDialogOpen(false); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this sponsored ad?')) return;
    try { await api.delete(`/sponsored-ads/${id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  const toggleActive = async (ad) => {
    try { await api.patch(`/sponsored-ads/${ad.id}`, { active: !ad.active }); load(); }
    catch { toast.error('Failed'); }
  };

  const placementLabels = {
    home_hero: 'Home — Hero',
    home_between: 'Home — Between sections',
    browse_top: 'Browse — Above list',
    listing_side: 'Listing detail — Sidebar',
    listing_grid: 'Listing / Home / Seller grids — Sponsored card',
    blog_card: 'Blog list — Sponsored card',
    blog_inline: 'Blog article — Inline banner',
  };

  return (
    <div className='space-y-3'>
      <div className='flex justify-end'>
        <Button onClick={() => { setEditing({ ...emptyAd }); setDialogOpen(true); }} data-testid='cms-new-ad'>
          <Plus className='h-4 w-4 mr-1' /> New sponsored ad
        </Button>
      </div>
      <Card className='divide-y'>
        {ads.length === 0 && <div className='p-8 text-center text-muted-foreground text-sm'>No sponsored ads yet.</div>}
        {ads.map(a => (
          <div key={a.id} className='p-4 flex items-start gap-3' data-testid={`cms-ad-row-${a.id}`}>
            <div className='h-16 w-24 rounded-md bg-secondary overflow-hidden shrink-0'>
              {a.image_url && <img src={a.image_url} alt='' className='w-full h-full object-cover' />}
            </div>
            <div className='flex-1 min-w-0'>
              <div className='flex flex-wrap items-center gap-1.5'>
                <div className='font-semibold text-sm truncate'>{a.title}</div>
                <span className={`text-[10px] rounded-full px-1.5 py-0.5 font-medium ${a.active ? 'bg-[hsl(var(--verified))]/10 text-[hsl(var(--verified))]' : 'bg-muted text-muted-foreground'}`}>
                  {a.active ? 'Active' : 'Inactive'}
                </span>
                <span className='text-[10px] rounded-full bg-secondary px-1.5 py-0.5'>{placementLabels[a.placement] || a.placement}</span>
                {a.listing_type && <span className='text-[10px] rounded-full bg-secondary px-1.5 py-0.5'>{a.listing_type}</span>}
              </div>
              {a.subtitle && <div className='text-xs text-muted-foreground truncate'>{a.subtitle}</div>}
              <div className='text-[11px] text-muted-foreground mt-1' data-testid={`cms-ad-stats-${a.id}`}>
                {a.advertiser_name || '—'} · <span data-testid={`cms-ad-impr-${a.id}`}>{(a.impression_count || 0).toLocaleString()} impr</span> · <span data-testid={`cms-ad-clicks-${a.id}`}>{(a.click_count || 0).toLocaleString()} clicks</span> · <span className='font-medium text-foreground' data-testid={`cms-ad-ctr-${a.id}`}>{a.impression_count ? ((a.click_count || 0) / a.impression_count * 100).toFixed(1) : '0.0'}% CTR</span>
              </div>
            </div>
            <div className='flex flex-col gap-1'>
              <Button size='sm' variant='outline' onClick={() => toggleActive(a)}>
                {a.active ? 'Pause' : 'Activate'}
              </Button>
              <Button size='sm' variant='outline' onClick={() => { setEditing(a); setDialogOpen(true); }} data-testid={`cms-edit-ad-${a.id}`}>Edit</Button>
              <Button size='sm' variant='outline' onClick={() => remove(a.id)} data-testid={`cms-delete-ad-${a.id}`}><Trash2 className='h-3.5 w-3.5' /></Button>
            </div>
          </div>
        ))}
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className='max-w-2xl' onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
          <DialogHeader><DialogTitle>{editing?.id ? 'Edit ad' : 'New sponsored ad'}</DialogTitle></DialogHeader>
          {editing && (
            <div className='space-y-3'>
              <div>
                <Label>Title *</Label>
                <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid='cms-ad-title' />
              </div>
              <div>
                <Label>Subtitle</Label>
                <Input value={editing.subtitle || ''} onChange={(e) => setEditing({ ...editing, subtitle: e.target.value })} />
              </div>
              <div className='grid grid-cols-2 gap-3'>
                <div>
                  <Label>Ad image *</Label>
                  <div className='space-y-2'>
                    {editing.image_url && (
                      <div className='relative rounded-md border overflow-hidden bg-muted'>
                        <img src={editing.image_url.startsWith('http') ? editing.image_url : `${process.env.REACT_APP_BACKEND_URL || ''}${editing.image_url}`} alt='' className='w-full h-24 object-cover' />
                      </div>
                    )}
                    <div className='flex gap-2 items-center'>
                      <label className='inline-flex items-center gap-1.5 rounded-md border px-3 py-2 cursor-pointer text-sm hover:bg-secondary'>
                        <Upload className='h-3.5 w-3.5' />
                        {uploadingAd ? 'Uploading…' : (editing.image_url ? 'Replace' : 'Upload')}
                        <input
                          type='file'
                          accept='image/*'
                          className='hidden'
                          disabled={uploadingAd}
                          onChange={async (e) => {
                            const f = e.target.files?.[0];
                            e.target.value = '';
                            if (!f) return;
                            setUploadingAd(true);
                            try {
                              // Sponsored ad creatives skip the IndusFact watermark
                              // — advertiser branding must not be corrupted (Review.pdf #13).
                              const r = await uploadFile(f, 'image', { context: 'sponsored_ad_image' });
                              const url = r.public_url?.startsWith('http') ? r.public_url : `${process.env.REACT_APP_BACKEND_URL || ''}${r.public_url}`;
                              setEditing({ ...editing, image_url: url });
                              toast.success('Ad image uploaded (no watermark applied)');
                            } catch (err) {
                              toast.error(extractApiError(err, 'Upload failed'));
                            } finally { setUploadingAd(false); }
                          }}
                          data-testid='cms-ad-image-upload'
                        />
                      </label>
                      <span className='text-[11px] text-muted-foreground'>or paste URL below</span>
                    </div>
                    <Input value={editing.image_url} onChange={(e) => setEditing({ ...editing, image_url: e.target.value })} placeholder='https://… (external image URL)' data-testid='cms-ad-image' />
                  </div>
                </div>
                <div>
                  <Label>Click URL *</Label>
                  <Input value={editing.click_url} onChange={(e) => setEditing({ ...editing, click_url: e.target.value })} placeholder='/sell?q=… or https://…' data-testid='cms-ad-click' />
                </div>
              </div>
              <div className='grid grid-cols-2 gap-3'>
                <div>
                  <Label>Placement</Label>
                  <Select value={editing.placement} onValueChange={(v) => setEditing({ ...editing, placement: v })}>
                    <SelectTrigger data-testid='cms-ad-placement'><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value='home_hero'>Home — Hero</SelectItem>
                      <SelectItem value='home_between'>Home — Between sections</SelectItem>
                      <SelectItem value='browse_top'>Browse — Above list</SelectItem>
                      <SelectItem value='listing_side'>Listing detail — Sidebar</SelectItem>
                      <SelectItem value='listing_grid'>Listing / Home / Seller grids — Sponsored card</SelectItem>
                      <SelectItem value='blog_card'>Blog list — Sponsored card</SelectItem>
                      <SelectItem value='blog_inline'>Blog article — Inline banner</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Listing type filter</Label>
                  <Select
                    value={editing.listing_type || 'any'}
                    onValueChange={(v) => setEditing({ ...editing, listing_type: v === 'any' ? null : v })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value='any'>Show everywhere</SelectItem>
                      <SelectItem value='sell'>Sell pages only</SelectItem>
                      <SelectItem value='wanted'>Wanted pages only</SelectItem>
                      <SelectItem value='rent'>Rent pages only</SelectItem>
                      <SelectItem value='service'>Service pages only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className='grid grid-cols-2 gap-3'>
                <div>
                  <Label>Advertiser name</Label>
                  <Input value={editing.advertiser_name || ''} onChange={(e) => setEditing({ ...editing, advertiser_name: e.target.value })} />
                </div>
                <div>
                  <Label>Display order</Label>
                  <Input type='number' value={editing.display_order || 0} onChange={(e) => setEditing({ ...editing, display_order: Number(e.target.value) })} />
                </div>
              </div>
              <div className='grid grid-cols-2 gap-3'>
                <div>
                  <Label>Starts at (IST)</Label>
                  <Input
                    type='datetime-local'
                    value={utcIsoToIstInput(editing.starts_at)}
                    onChange={(e) => setEditing({ ...editing, starts_at: istInputToUtcIso(e.target.value) })}
                    data-testid='cms-ad-starts-at'
                  />
                  <div className='text-[10px] text-muted-foreground mt-1'>India time (UTC+5:30). Optional — leave blank to run immediately.</div>
                </div>
                <div>
                  <Label>Ends at (IST)</Label>
                  <Input
                    type='datetime-local'
                    value={utcIsoToIstInput(editing.ends_at)}
                    onChange={(e) => setEditing({ ...editing, ends_at: istInputToUtcIso(e.target.value) })}
                    data-testid='cms-ad-ends-at'
                  />
                  <div className='text-[10px] text-muted-foreground mt-1'>India time (UTC+5:30). Optional — blank means no expiry.</div>
                </div>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea rows={2} value={editing.notes || ''} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} />
              </div>
              <div className='flex items-center gap-2'>
                <Switch checked={!!editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} />
                <Label>Active</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant='outline' onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid='cms-ad-save'><Save className='h-4 w-4 mr-1' /> Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// ============================================================
// Analytics tab — GA4 + Meta Pixel configuration
// ============================================================
const AnalyticsTab = () => {
  const [form, setForm] = useState({
    ga4_measurement_id: '',
    meta_pixel_id: '',
    tracking_enabled: true,
    exclude_staff: true,
    consent_required: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/settings/analytics');
      const cur = data.analytics || {};
      setForm(f => ({
        ...f,
        ga4_measurement_id: cur.ga4_measurement_id || '',
        meta_pixel_id: cur.meta_pixel_id || '',
        tracking_enabled: cur.tracking_enabled !== false,
        exclude_staff: cur.exclude_staff !== false,
        consent_required: cur.consent_required !== false,
      }));
    } catch (e) {
      toast.error('Failed to load analytics settings');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch('/admin/settings/analytics', { updates: form });
      toast.success('Analytics settings saved');
      load();
    } catch (e) {
      toast.error(extractApiError(e) || 'Save failed');
    } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm('Reset analytics settings to defaults? This clears both provider IDs.')) return;
    try {
      await api.post('/admin/settings/analytics/reset');
      toast.success('Reset to defaults');
      load();
    } catch (e) { toast.error('Reset failed'); }
  };

  if (loading) return <div className='p-8 text-center text-muted-foreground text-sm'>Loading…</div>;

  return (
    <div className='space-y-4 max-w-3xl' data-testid='admin-analytics-tab'>
      <div className='rounded-lg border bg-secondary/30 p-4 text-xs text-muted-foreground'>
        <div className='font-semibold text-foreground mb-1'>How this works</div>
        Provider scripts (Google Analytics 4 &amp; Meta Pixel) load on the site the moment a valid ID is saved here — no redeploy needed. Leave a field blank to disable that provider. Staff (admin / moderator) sessions bypass tracking by default so internal QA does not pollute your marketing data.
      </div>

      <Card className='p-5 space-y-4'>
        <div>
          <div className='font-semibold text-sm'>Google Analytics 4</div>
          <p className='text-xs text-muted-foreground mt-0.5'>Measurement ID from your GA4 property (analytics.google.com → Admin → Data Streams). Starts with <code className='px-1 rounded bg-secondary'>G-</code>.</p>
        </div>
        <div>
          <Label>GA4 Measurement ID</Label>
          <Input
            value={form.ga4_measurement_id}
            onChange={(e) => setForm(f => ({ ...f, ga4_measurement_id: e.target.value }))}
            placeholder='G-XXXXXXXXXX'
            data-testid='analytics-ga4-input'
            className='font-mono'
          />
          <div className='text-[11px] text-muted-foreground mt-1'>Leave blank to disable Google Analytics.</div>
        </div>
      </Card>

      <Card className='p-5 space-y-4'>
        <div>
          <div className='font-semibold text-sm'>Meta Pixel (Facebook / Instagram)</div>
          <p className='text-xs text-muted-foreground mt-0.5'>Pixel ID from Meta Events Manager (business.facebook.com → Events Manager → Data Sources). 15–16 digit number.</p>
        </div>
        <div>
          <Label>Meta Pixel ID</Label>
          <Input
            value={form.meta_pixel_id}
            onChange={(e) => setForm(f => ({ ...f, meta_pixel_id: e.target.value }))}
            placeholder='1234567890123456'
            data-testid='analytics-pixel-input'
            className='font-mono'
          />
          <div className='text-[11px] text-muted-foreground mt-1'>Leave blank to disable Meta Pixel.</div>
        </div>
      </Card>

      <Card className='p-5 space-y-4'>
        <div className='font-semibold text-sm'>Tracking preferences</div>

        <div className='flex items-start justify-between gap-4'>
          <div>
            <Label>Tracking enabled</Label>
            <div className='text-[11px] text-muted-foreground mt-0.5'>Master switch. Turn off during incidents or staging deploys to stop all data collection without wiping IDs.</div>
          </div>
          <Switch
            checked={!!form.tracking_enabled}
            onCheckedChange={(v) => setForm(f => ({ ...f, tracking_enabled: v }))}
            data-testid='analytics-toggle-enabled'
          />
        </div>

        <div className='flex items-start justify-between gap-4 pt-3 border-t'>
          <div>
            <Label>Exclude staff sessions</Label>
            <div className='text-[11px] text-muted-foreground mt-0.5'>Skip tracking for admins &amp; moderators — highly recommended so internal reviews don't inflate analytics.</div>
          </div>
          <Switch
            checked={!!form.exclude_staff}
            onCheckedChange={(v) => setForm(f => ({ ...f, exclude_staff: v }))}
            data-testid='analytics-toggle-exclude-staff'
          />
        </div>

        <div className='flex items-start justify-between gap-4 pt-3 border-t'>
          <div>
            <Label>Show consent banner</Label>
            <div className='text-[11px] text-muted-foreground mt-0.5'>DPDP (India) &amp; GDPR (EU) friendly — new visitors must accept before tracking loads. Turn off only if your legal review says otherwise.</div>
          </div>
          <Switch
            checked={!!form.consent_required}
            onCheckedChange={(v) => setForm(f => ({ ...f, consent_required: v }))}
            data-testid='analytics-toggle-consent'
          />
        </div>
      </Card>

      <div className='flex items-center gap-2'>
        <Button onClick={save} disabled={saving} data-testid='analytics-save'>
          <Save className='h-4 w-4 mr-1' /> {saving ? 'Saving…' : 'Save settings'}
        </Button>
        <Button variant='outline' onClick={reset} data-testid='analytics-reset'>Reset to defaults</Button>
      </div>

      <div className='rounded-lg border border-dashed border-primary/30 bg-primary/5 p-4 text-xs text-muted-foreground'>
        <div className='font-semibold text-foreground mb-1'>Events being tracked</div>
        <ul className='list-disc pl-4 space-y-0.5'>
          <li><code className='text-[11px]'>sign_up</code>, <code className='text-[11px]'>login</code> — new account &amp; return visits</li>
          <li><code className='text-[11px]'>listing_view</code>, <code className='text-[11px]'>listing_posted</code> — supply &amp; discovery signals</li>
          <li><code className='text-[11px]'>search</code> — buyer query with result count</li>
          <li><code className='text-[11px]'>contact_reveal</code>, <code className='text-[11px]'>inquiry_sent</code> — primary demand conversions</li>
          <li><code className='text-[11px]'>plan_page_view</code>, <code className='text-[11px]'>checkout_started</code>, <code className='text-[11px]'>payment_success</code> — revenue funnel</li>
        </ul>
      </div>
    </div>
  );
};


export default AdminCMS;
